import { wrap } from 'comlink';
import type { ExposedEndpoint } from './RenderInfo.worker';
import { AsyncQueue, BoundedBufferMap } from './utils';
import { LZ4DecodedChunkLayout, type LZ4Decoder, createDecoder } from './utils/decodeBinding';

import RenderInfoWorkerScript from './RenderInfo.worker.ts?worker&inline';
import type { ChunkInfo, Point, StarterType } from './store';
import type TrackView from './utils/TrackView';
import { starterIdToBuffer } from './utils/sharedDecode';

type ChunkId = string;

export const RenderInfoWorker = wrap<ExposedEndpoint>(new RenderInfoWorkerScript());

function intersects(start0: number, end0: number, start1: number, end1: number) {
  return !(start1 > end0 || end1 < start0);
}

function getStarterIdBuffer(starters: Array<StarterType>) {
  const output = new Uint8Array(starters.length * 12);
  for (let i = 0; i < starters.length; i++) {
    const starterId = starters[i].id;
    if (starterId != null) {
      const buffer = new Uint8Array(starterIdToBuffer(starterId));
      output.set(buffer, i * 12);
    }
  }
  return output.buffer;
}

export default class ChunkManager {
  decodeQueue: AsyncQueue<ChunkId, ArrayBufferLike | null> = new AsyncQueue(3);
  chunkCache: BoundedBufferMap<ChunkId> = new BoundedBufferMap(1024 * 1024 * 200); // 200 MB
  eventId: string;
  chunkInfos: Array<ChunkInfo>;
  starterIdBuffer: ArrayBufferLike;
  starterIds: Array<string>;
  chunkedShadowTrack: TrackView | null;

  static create(
    eventId: string,
    chunkInfos: Array<ChunkInfo>,
    starters: Array<StarterType>,
    chunkedShadowTrack: TrackView | null,
  ): ChunkManager {
    return new ChunkManager(
      eventId,
      chunkInfos,
      getStarterIdBuffer(starters),
      starters.map((s) => s.id),
      chunkedShadowTrack,
    );
  }

  constructor(
    eventId: string,
    chunkInfos: Array<ChunkInfo>,
    starterIdBuffer: ArrayBufferLike,
    starterIds: Array<string>,
    chunkedShadowTrack: TrackView | null,
  ) {
    this.eventId = eventId;
    this.chunkInfos = chunkInfos;
    this.starterIdBuffer = starterIdBuffer;
    this.starterIds = starterIds;
    this.chunkedShadowTrack = chunkedShadowTrack;
  }

  getChunkInfo(time: number): ChunkInfo | null {
    return (
      this.chunkInfos.find(
        (chunkInfo) => chunkInfo.startTime <= time && time < chunkInfo.endTime,
      ) || null
    );
  }

  getNextChunkInfo(chunkInfo: ChunkInfo): ChunkInfo | null {
    let direction = 1;
    if (this.chunkCache.lru.length > 2) {
      direction =
        this.chunkCache.lru[0] < this.chunkCache.lru[1] &&
        this.chunkCache.lru[1] < this.chunkCache.lru[2]
          ? -1
          : 1;
    }

    if (direction === -1) {
      return this.chunkInfos
        .filter((c) => c.startTime < chunkInfo.startTime)
        .sort((a, b) => b.startTime - a.startTime)[0];
    }
    return this.chunkInfos
      .filter((c) => c.startTime >= chunkInfo.endTime)
      .sort((a, b) => b.startTime - a.startTime)[0];
  }

  getChunkSync(time: number): ArrayBufferLike | null {
    const chunkInfo = this.getChunkInfo(time);
    if (chunkInfo == null) return null;
    const cachedChunk = this.chunkCache.get(chunkInfo.id);
    if (cachedChunk != null) {
      const nextChunkInfo = this.getNextChunkInfo(chunkInfo);
      if (nextChunkInfo != null) {
        this.triggerDecode(nextChunkInfo);
      }
      return cachedChunk;
    }
    this.triggerDecode(chunkInfo);
    return null;
  }

  getChunksSync(
    startTime: number,
    endTime: number,
  ): Array<{ startTime: number; endTime: number; buffer: ArrayBufferLike }> {
    const output = [];
    const chunkInfos = this.chunkInfos.filter((chunkInfo) =>
      intersects(startTime, endTime, chunkInfo.startTime, chunkInfo.endTime),
    );
    for (const chunkInfo of chunkInfos) {
      const cachedChunk = this.chunkCache.get(chunkInfo.id);
      if (cachedChunk != null) {
        output.push({
          startTime: chunkInfo.startTime,
          endTime: chunkInfo.endTime,
          buffer: cachedChunk,
        });
      } else {
        this.triggerDecode(chunkInfo);
      }
    }
    return output;
  }

  getPointsSync(
    starterId: string,
    startTime: number,
    endTime: number,
    withLastPoint = false,
  ): Array<Point> {
    const output: Array<Point> = [];
    const chunks = this.getChunksSync(startTime, endTime);
    for (const chunkPackage of chunks) {
      const decodedChunk = LZ4DecodedChunkLayout.from(chunkPackage.buffer);
      const trackIndex = decodedChunk.findTrackIndex(starterId);
      if (trackIndex !== -1) {
        const track = decodedChunk.getTrack(trackIndex);
        if (track.pointCount > 0) {
          let time = track.getTimestamp(0);
          for (let j = 0; j < track.pointCount; j++) {
            const nextTime = track.getTimestamp(Math.min(j + 1, track.pointCount - 1));
            if (
              startTime <= (withLastPoint ? nextTime : time) &&
              time < endTime &&
              chunkPackage.startTime <= time &&
              time <= chunkPackage.endTime
            ) {
              const point = track.getPoint(j);
              output.push(point);
            }
            time = nextTime;
          }
        }
      }
    }
    return output;
  }

  add(chunkInfo: ChunkInfo): void {
    const existingIndex = this.chunkInfos.findIndex(
      (other) => other.startTime <= chunkInfo.startTime && chunkInfo.endTime <= other.endTime,
    );
    if (existingIndex >= 0) {
      const existingChunkInfo = this.chunkInfos[existingIndex];
      if (
        existingChunkInfo.startTime === chunkInfo.startTime &&
        existingChunkInfo.endTime === chunkInfo.endTime
      ) {
        // When we have an exact match, replace the chunk and invalidate the cache
        this.chunkInfos[existingIndex] = chunkInfo;
        if (this.chunkCache.has(chunkInfo.id) || this.decodeQueue.has(chunkInfo.id)) {
          // Replace decompressed chunk in cache or queue
          this.decodeQueue
            .add(
              chunkInfo.id,
              () =>
                RenderInfoWorker.decodeCompressedChunk(
                  this.eventId,
                  chunkInfo.compressedBuffer,
                  this.starterIdBuffer,
                  chunkInfo.encodedLength,
                ),
              { force: true },
            )
            .then((decodedChunk) => {
              if (decodedChunk != null) {
                this.chunkCache.set(chunkInfo.id, decodedChunk);
              }
            })
            .catch(() => {
              // pass
            });
        }
      } else {
        // When we have a partial match (i.e. another chunk overlaps with this chunk),
        // we trim the existing chunk
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        existingChunkInfo.endTime = chunkInfo.startTime;
        this.chunkInfos.splice(existingIndex, 0, chunkInfo);
      }
    } else {
      const nextIndex = this.chunkInfos.findIndex((c) => c.startTime >= chunkInfo.endTime);
      if (nextIndex === -1) {
        this.chunkInfos.push(chunkInfo);
      } else {
        this.chunkInfos.splice(nextIndex, 0, chunkInfo);
      }
    }
  }

  triggerDecode(chunkInfo: ChunkInfo): void {
    if (this.chunkCache.has(chunkInfo.id) || this.decodeQueue.has(chunkInfo.id)) {
      return;
    }
    this.decodeQueue
      .add(chunkInfo.id, () =>
        RenderInfoWorker.decodeCompressedChunk(
          this.eventId,
          chunkInfo.compressedBuffer,
          this.starterIdBuffer,
          chunkInfo.encodedLength,
        ),
      )
      .then((decodedChunk) => {
        if (decodedChunk != null) {
          this.chunkCache.set(chunkInfo.id, decodedChunk);
          // console.log(new Date(chunkInfo.startTime), new Date(chunkInfo.endTime));
          // console.table(DecodedChunkLayout.from(decodedChunk).debug());
        }
      })
      .catch(() => {
        // pass
      });
  }

  async createDecoder(): Promise<LZ4Decoder> {
    const decoder = await createDecoder();
    if (this.chunkedShadowTrack != null) {
      decoder.importShadowTrack(this.chunkedShadowTrack.track.buffer);
    }
    decoder.importStarterIds(this.starterIdBuffer);
    return decoder;
  }
}
